import React from "react";
import { clamp } from "@/lib/utils/marketSimilarity/tableUtils";

type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  min?: number;
  max?: number;
  value?: number;
  onValueChange: (next: number) => void;
  allowEmpty?: boolean;
};

export function NumberField({
  id,
  label,
  min,
  max,
  value,
  onValueChange,
  allowEmpty,
  ...rest
}: Props) {
  // Extract any onFocus passed in so we can compose it with our select-on-focus behavior
  const { onFocus: userOnFocus, ...inputProps } = rest;

  return (
    <div>
      <label className="block text-sm font-medium mb-1" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        className="w-full border rounded px-3 py-2"
        value={Number.isFinite(value) ? value : ""}
        onFocus={(e) => {
          // Select the entire contents when the field gains focus
          // so users can immediately type to replace the current value
          e.currentTarget.select();
          // Call any user-provided onFocus handler
          if (typeof userOnFocus === "function")
            userOnFocus(e as unknown as React.FocusEvent<HTMLInputElement>);
        }}
        onChange={(e) => {
          const raw = e.target.value;
          if (allowEmpty && raw === "") {
            // Allow the controlled value to be cleared without coercing to 0
            onValueChange(undefined as unknown as number);
            return;
          }
          const n = Number(raw);
          if (Number.isNaN(n)) return;
          const next =
            min !== undefined && max !== undefined ? clamp(n, min, max) : n;
          onValueChange(next);
        }}
        {...inputProps}
      />
    </div>
  );
}
